import { Button, useAlert } from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ClassModuleEmptyState,
  ClassModuleFilterBar,
  ClassModuleListTemplate,
  ClassModuleTable,
  DeleteConfirmModal,
  useClassModuleFilters,
  useClassModuleList,
  useDeleteClassModule,
} from '@/features/class-module-management';
import type { ClassModuleListItemViewModel } from '@/shared/api/generated';

export const Route = createFileRoute('/_authenticated/class-modules/')({
  component: ClassModuleListPage,
});

/** Página de listagem de módulos de aula. */
export function ClassModuleListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('classModuleManagement');
  const { alert } = useAlert();

  const [confirming, setConfirming] = useState<ClassModuleListItemViewModel | null>(null);

  const { data, isLoading, isError, currentPage, hasNextPage, totalPages, totalCount } =
    useClassModuleList();
  const { filters, updateFilters } = useClassModuleFilters();
  const deleteModule = useDeleteClassModule();

  // Sem resultados por filtro (ou erro de carregamento) é diferente de não haver
  // nenhum módulo: só exibimos o empty state "crie seu primeiro módulo" quando a
  // lista está genuinamente vazia — sem filtros ativos e sem erro.
  const hasActiveFilters =
    filters.description !== '' ||
    filters.companyId !== null ||
    filters.classStudentId !== null ||
    filters.dateStart !== '' ||
    filters.dateFinish !== '';
  const showEmptyState = !isLoading && !isError && data.length === 0 && !hasActiveFilters;

  const handleDeleteConfirm = () => {
    if (confirming?.id === undefined) return;
    deleteModule.mutate(
      { id: confirming.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('toast.deleteError') }),
        onSettled: () => setConfirming(null),
      },
    );
  };

  return (
    <>
      <ClassModuleListTemplate
        subtitle={t('list.subtitle', { count: totalCount ?? data.length })}
        headerActions={
          <Button
            hierarchy="primary"
            size="lg"
            leadingIcon={PlusIcon}
            onClick={() => void navigate({ to: '/class-modules/create' })}
          >
            {t('list.createButton')}
          </Button>
        }
        filterPanel={<ClassModuleFilterBar />}
        table={
          showEmptyState ? (
            <ClassModuleEmptyState
              onCreate={() => void navigate({ to: '/class-modules/create' })}
            />
          ) : (
            <ClassModuleTable
              data={data}
              isLoading={isLoading}
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={page => updateFilters({ page })}
              onEdit={id => void navigate({ to: '/class-modules/$id', params: { id: String(id) } })}
              onDelete={setConfirming}
            />
          )
        }
      />

      <DeleteConfirmModal
        open={confirming !== null}
        itemName={confirming?.description ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setConfirming(null);
        }}
      />
    </>
  );
}
